import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { Exact } from "@Helpers/types/exact";

export type MinimizerItem = {
  path: string;
  text: string;
  index?: number;
  /**
   * New-shell dock (plan-ds §4.10), additive and optional: the file extension ("rvt") and the "where" line
   * ("CUBE Clinic · v12"). The classic `handleMinimizeViewer` writes neither; the dock derives them.
   */
  ext?: string;
  subtitle?: string;
};

const minimizeFloatterSlice = createSlice({
  name: "minimizeFloatterData",
  initialState: [] as MinimizerItem[],
  reducers: {
    setMinimizerData: (state, action: PayloadAction<MinimizerItem>) => {
      const checkForExistingEntry = state.find((item) => item.path == action.payload.path);
      if (!checkForExistingEntry) {
        const data = [...state, action.payload];
        return data.map((item, index: number) => ({
          ...item,
          index,
        }));
      }
      return state;
    },
    removeMinimizerData: (state, action: PayloadAction<number>) => {
      const data = [...state];
      data.splice(action.payload, 1);
      const result = data.map((record, index: number) => ({ ...record, index }));
      return result;
    },
    emptyMinimizerData: () => [],
  },
});

const {
  setMinimizerData: _setMinimizerDataRaw,
  removeMinimizerData,
  emptyMinimizerData,
} = minimizeFloatterSlice.actions;

/**
 * Exact<>-wrapped: object payload, catches excess properties via variables.
 *
 * `Exact<>` is applied to the PARAMETER (`T & Exact<…>`) rather than used as the constraint — the
 * same form `contracts.reducer.ts` documents. Under `exactOptionalPropertyTypes` type-fest's
 * `ExactObject` homomorphic mapping re-emits every optional member as `k?: V | undefined`, so a
 * bare `T extends Exact<MinimizerItem, T>` no longer proves `T` is a `MinimizerItem`.
 */
export const setMinimizerData = <T extends MinimizerItem>(payload: T & Exact<MinimizerItem, T>) =>
  _setMinimizerDataRaw(payload);
export { removeMinimizerData, emptyMinimizerData };

export const minimizeFloatterData = minimizeFloatterSlice.reducer;

export default minimizeFloatterSlice.reducer;
